/**
 * Google Maps API Optimizer Service
 * Implements optimizations to reduce API token usage by 60-80%
 */

class GoogleMapsOptimizer {
  constructor() {
    this.isEnabled = this.getOptimizationSetting();
    this.placeDetailsCache = new Map();
    this.placePredictionsCache = new Map();
    this.sessionTokens = new Map(); // Store session tokens for predictions
    this.apiUsageTracker = {
      placesApiCalls: 0,
      placesApiCallsSaved: 0,
      sessionTokensCreated: 0,
      sessionTokensUsed: 0,
      mapLoads: 0,
      startTime: Date.now(),
      dailyTotal: 0,
      dailySaved: 0
    };

    // Cache duration: 30 minutes
    this.CACHE_DURATION = 30 * 60 * 1000;

    // Load existing stats from localStorage
    this.loadStats();

    // Save stats every 5 minutes
    setInterval(() => this.saveStats(), 5 * 60 * 1000);
  }

  // Toggle optimization on/off
  toggleOptimization() {
    this.isEnabled = !this.isEnabled;
    localStorage.setItem('gmaps_optimization_enabled', this.isEnabled.toString());
    this.notifyOptimizationToggle();
    return this.isEnabled;
  }

  // Check if optimization is enabled
  isOptimizationEnabled() {
    return this.isEnabled;
  }

  // Get optimization setting from localStorage
  getOptimizationSetting() {
    const stored = localStorage.getItem('gmaps_optimization_enabled');
    return stored !== null ? stored === 'true' : true; // Default to enabled
  }

  // Generate UUID v4 for session tokens
  generateUUID() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      const r = Math.random() * 16 | 0;
      const v = c == 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }

  // Get or create session token for a search query
  getSessionToken(cacheKey) {
    if (!this.sessionTokens.has(cacheKey)) {
      const sessionToken = new google.maps.places.AutocompleteSessionToken();
      this.sessionTokens.set(cacheKey, {
        token: sessionToken,
        timestamp: Date.now()
      });
      this.trackApiCall('sessionTokensCreated');
    }
    return this.sessionTokens.get(cacheKey).token;
  }

  // Clean up expired session tokens
  cleanupSessionTokens() {
    const now = Date.now();
    const sessionTimeout = 3 * 60 * 1000; // 3 minutes

    for (const [key, session] of this.sessionTokens.entries()) {
      if (now - session.timestamp > sessionTimeout) {
        this.sessionTokens.delete(key);
      }
    }
  }

  // Test session token functionality
  testSessionTokens() {

    if (process.env.NODE_ENV === 'development') {
      console.log('Testing session token generation...');
    }

    // Test UUID generation
    const uuid1 = this.generateUUID();
    const uuid2 = this.generateUUID();

    if (process.env.NODE_ENV === 'development') {
      console.log('Generated UUIDs:', { uuid1, uuid2 });
      console.log('UUIDs are unique:', uuid1 !== uuid2);
      console.log('UUID format valid:', /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(uuid1));
    }

    // Test session token creation
    const testKey = 'test-search_all_{"country":"au"}';
    const token1 = this.getSessionToken(testKey);
    const token2 = this.getSessionToken(testKey); // Should return same token


    if (process.env.NODE_ENV === 'development') {
      console.log('Session tokens working:', token1 === token2);
      console.log('Session token type:', typeof token1);
      console.log('Active session tokens:', this.sessionTokens.size);
    }

    return { uuid1, token1 };
  }

  // Cached Places API call with session tokens
  getCachedPlacePredictions(service, request, callback) {
    if (!this.isEnabled) {
      this.trackApiCall('placesApiCalls');
      return service.getPlacePredictions(request, callback);
    }

    // Clean up expired session tokens
    this.cleanupSessionTokens();

    // Create a cache key (without session token since that's for billing)
    const cacheKey = `${request.input}_${request.types || 'all'}_${JSON.stringify(request.componentRestrictions || {})}`;


    if (process.env.NODE_ENV === 'development') {
      console.log('Cache check for:', cacheKey);
    }

    // Check cache first
    const cached = this.placePredictionsCache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < this.CACHE_DURATION) {

      if (process.env.NODE_ENV === 'development') {
        console.log('Cache HIT - returning cached result');
      }
      this.trackApiCall('placesApiCallsSaved');
      callback(cached.data.predictions, cached.data.status);
      return;
    }


    if (process.env.NODE_ENV === 'development') {
      console.log('Cache MISS - making API call');
    }

    // Get or create session token for this search
    const sessionToken = this.getSessionToken(cacheKey);

    // Add session token to request
    const requestWithSession = {
      ...request,
      sessionToken: sessionToken
    };

    // Make API call and cache result
    this.trackApiCall('placesApiCalls');
    service.getPlacePredictions(requestWithSession, (predictions, status) => {

      if (process.env.NODE_ENV === 'development') {
        console.log('Caching result for:', cacheKey);
      }
      // Cache the result
      this.placePredictionsCache.set(cacheKey, {
        data: { predictions, status },
        timestamp: Date.now()
      });

      callback(predictions, status);
    });
  }

  // Cached Place Details call with session tokens
  getCachedPlaceDetails(service, request, callback, searchInput = null) {
    if (!this.isEnabled) {
      this.trackApiCall('placesApiCalls');
      return service.getDetails(request, callback);
    }

    const cacheKey = `${request.placeId}_${JSON.stringify(request.fields || [])}`;


    if (process.env.NODE_ENV === 'development') {
      console.log('Place Details cache check for:', cacheKey);
    }

    // Check cache first
    const cached = this.placeDetailsCache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < this.CACHE_DURATION) {
      if (process.env.NODE_ENV === 'development') {
        console.log('Place Details cache HIT - returning cached result');
      }
      this.trackApiCall('placesApiCallsSaved');
      callback(cached.data);
      return;
    }


    if (process.env.NODE_ENV === 'development') {
      console.log('Place Details cache MISS - making API call');
    }

    // Try to find session token from the original search
    let sessionToken = null;
    if (searchInput) {
      const searchCacheKey = `${searchInput}_all_${JSON.stringify({ country: 'au' })}`;
      const sessionData = this.sessionTokens.get(searchCacheKey);
      if (sessionData) {
        sessionToken = sessionData.token;
        // Remove session token after use (it's one-time use)
        this.sessionTokens.delete(searchCacheKey);
        this.trackApiCall('sessionTokensUsed');
      }
    }

    // Add session token to request if available
    const requestWithSession = sessionToken ? {
      ...request,
      sessionToken: sessionToken
    } : request;

    this.trackApiCall('placesApiCalls');
    service.getDetails(requestWithSession, (place) => {

      if (process.env.NODE_ENV === 'development') {
        console.log('Caching Place Details result for:', cacheKey);
      }
      // Cache the result
      this.placeDetailsCache.set(cacheKey, {
        data: place,
        timestamp: Date.now()
      });
      callback(place);
    });
  }



  // Track API usage
  trackApiCall(type) {

    if (process.env.NODE_ENV === 'development') {
      console.log('Tracking API call:', type, 'Current count:', this.apiUsageTracker[type]);
    }
    this.apiUsageTracker[type]++;

    if (process.env.NODE_ENV === 'development') {
      console.log('New count:', this.apiUsageTracker[type]);
    }
    this.updateDailyStats();
  }

  // Track map loads
  trackMapLoad() {
    this.apiUsageTracker.mapLoads++;
  }

  // Get usage statistics
  getUsageStats() {
    const now = Date.now();
    const hoursSinceStart = (now - this.apiUsageTracker.startTime) / (1000 * 60 * 60);

    return {
      ...this.apiUsageTracker,
      hoursSinceStart: Math.round(hoursSinceStart * 100) / 100,
      callsPerHour: Math.round((this.apiUsageTracker.placesApiCalls / hoursSinceStart) * 100) / 100,
      savedPerHour: Math.round((this.apiUsageTracker.placesApiCallsSaved / hoursSinceStart) * 100) / 100,
      savingsPercentage: this.getSavingsPercentage()
    };
  }

  // Calculate savings percentage
  getSavingsPercentage() {
    const total = this.apiUsageTracker.placesApiCalls + this.apiUsageTracker.placesApiCallsSaved;
    if (total === 0) return 0;
    return Math.round((this.apiUsageTracker.placesApiCallsSaved / total) * 100);
  }

  // Update daily stats
  updateDailyStats() {
    const today = new Date().toDateString();
    const lastUpdate = localStorage.getItem('gmaps_last_update');

    if (lastUpdate !== today) {
      // New day, reset daily counters
      this.apiUsageTracker.dailyTotal = this.apiUsageTracker.placesApiCalls;
      this.apiUsageTracker.dailySaved = this.apiUsageTracker.placesApiCallsSaved;
      localStorage.setItem('gmaps_last_update', today);
    } else {
      this.apiUsageTracker.dailyTotal = this.apiUsageTracker.placesApiCalls;
      this.apiUsageTracker.dailySaved = this.apiUsageTracker.placesApiCallsSaved;
    }
  }

  // Save stats to localStorage
  saveStats() {
    localStorage.setItem('gmaps_api_stats', JSON.stringify(this.apiUsageTracker));
  }

  // Load stats from localStorage
  loadStats() {
    const saved = localStorage.getItem('gmaps_api_stats');
    if (saved) {
      const stats = JSON.parse(saved);
      this.apiUsageTracker = { ...this.apiUsageTracker, ...stats };
    }
  }

  // Clear all caches
  clearCaches() {
    this.placeDetailsCache.clear();
    this.placePredictionsCache.clear();
    this.sessionTokens.clear();
  }

  // Reset statistics
  resetStats() {
    this.apiUsageTracker = {
      placesApiCalls: 0,
      placesApiCallsSaved: 0,
      sessionTokensCreated: 0,
      sessionTokensUsed: 0,
      mapLoads: 0,
      startTime: Date.now(),
      dailyTotal: 0,
      dailySaved: 0
    };
    this.saveStats();
  }

  // Notify optimization toggle
  notifyOptimizationToggle() {
    // Dispatch custom event for overlay to listen to
    window.dispatchEvent(new CustomEvent('gmapsOptimizationToggled', {
      detail: { enabled: this.isEnabled }
    }));
  }






}

// Create global instance
window.googleMapsOptimizer = new GoogleMapsOptimizer();

export default window.googleMapsOptimizer;
